import React from 'react';
import {
	Col,
	Empty,
	Flex,
	Image,
	Row,
	Space,
	Upload,
} from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import type { FixingCompanyWithLogo } from '@api/features/fixing-companies/getFixingCompanies';
import type { BankAccountWithCurrencyFlag } from '@api/features/vessels/getVesselDetails';
import EditableDetails from '@client/components/EditableDetails/EditableDetails';
import Button from '@client/components/Button';
import Card from '@client/components/Card/Card';
import {
	updateFixingCompany,
	updateFixingCompanyLogo,
} from '@client/lib/api';
import showErrorNotification from '@client/utils/showErrorNotification';
import FixingCompanyBankAccounts from './FixingCompanyBankAccounts';
import getFixingCompanyItems, { FixingCompanyDetailsValues } from './helpers/getFixingCompanyItems';

type Props = {
	fixingCompany: FixingCompanyWithLogo;
	fixingCompanies: Array<FixingCompanyWithLogo>;
	bankAccounts: Array<BankAccountWithCurrencyFlag>;
	refreshFixingCompanies: () => Promise<void> | void;
	refreshBankAccounts: () => Promise<unknown> | void;
};

const FixingCompanyDetails = ({
	fixingCompany,
	fixingCompanies,
	bankAccounts,
	refreshFixingCompanies,
	refreshBankAccounts,
}: Props) => {
	const onSave = async (values: Partial<FixingCompanyDetailsValues>) => {
		await updateFixingCompany(fixingCompany.id, values);
		await refreshFixingCompanies();
	};

	const onUpdateLogo = async (file?: File) => {
		try {
			await updateFixingCompanyLogo(fixingCompany.id, file);
			await refreshFixingCompanies();
		} catch (e) {
			showErrorNotification('Could not update logo', e as Error);
		}

		return false;
	};

	return (
		<Row gutter={[16, 16]}>
			<Col xs={24} xxl={12}>
				<Flex vertical gap={16}>
					<EditableDetails<FixingCompanyDetailsValues>
						title="Company details"
						items={getFixingCompanyItems(fixingCompany)}
						onSave={onSave}
					/>
					<Card
						slim
						bordered
						title="Logo"
						extra={(
							<Space>
								{fixingCompany.logoUrl != null && (
									<Button
										type="link"
										danger
										icon={(<DeleteOutlined />)}
										onClick={() => onUpdateLogo()}
										confirmTitle="Are you sure you want to remove the logo?"
									/>
								)}
								<Upload
									beforeUpload={(file) => onUpdateLogo(file)}
									showUploadList={false}
								>
									<Button type="primary">Upload new</Button>
								</Upload>
							</Space>
						)}
					>
						{fixingCompany.logoUrl == null ? (
							<Empty description="No logo provided" />
						) : (
							<Image
								width={200}
								src={fixingCompany.logoUrl}
							/>
						)}
					</Card>
				</Flex>
			</Col>
			<Col xs={24} xxl={12}>
				<FixingCompanyBankAccounts
					fixingCompany={fixingCompany}
					fixingCompanies={fixingCompanies}
					bankAccounts={bankAccounts}
					refreshBankAccounts={refreshBankAccounts}
				/>
			</Col>
		</Row>
	);
};

export default FixingCompanyDetails;
